import { Component, DestroyRef, Injector, OnDestroy, ViewChild, afterNextRender, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, ReactiveFormsModule, FormBuilder, FormArray, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { ChartOfAccountModel } from '../../../../core/models/data/chart-of-account.model';
import { BudgetFrequency, ChartOfAccountCategory, DataEntityTypes, PropertyStatuses } from '../../../../core/enums/data.enums';
import { FinancialsPermission } from '../../../../core/enums/permissions.enums';
import { FinancialService } from '../../../../core/services/financial/financial.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ConfirmationModalComponent } from '../../../../parts/confirmation-modal/confirmation-modal.component';
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { ChartOfAccountTypeaheadComponent } from '../../../../parts/chart-of-account-typeahead/chart-of-account-typeahead.component';
import { AmountInput } from '../../../../parts/amount-input/amount-input.component';
import { InputLabel } from '../../../../parts/input-label/input-label.component';
import { IfHasPermissionDirective } from '../../../../core/directives/if-has-permission/if-has-permission.directive';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { BudgetMessagingConstants, GenericMessagingConstants } from '../../../../core/constants/messaging.constants';
import { CurrencyUtilities } from '../../../../core/utilities/currency.utilities';
import { BudgetModel, CreateBudgetLineItemRequest, CreateBudgetRequest, UpdateBudgetRequest } from '../../../../core/models/data/budget.model';
import { ApiResponseUtilities } from '../../../../core/utilities/api-response.utilities';
import { BudgetsFilter } from '../../../../core/filters/budgets.filter';
import { BudgetListSortOptions } from '../../../../core/enums/sort-options.enums';
import { ListConstants } from '../../../../core/constants/lists.constants';
import { CanComponentDeactivate } from '../../../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../../../core/services/dirty-form-registry.service';
import { PageTitleComponent } from '../../../../parts/page-title/page-title.component';

@Component({
	selector: 'ms-budget-entry',
	standalone: true,
	imports: [
		PageTitleComponent,
		CommonModule,
		ReactiveFormsModule,
		FaDuotoneIconComponent,
		ConfirmationModalComponent,
		InfoModalComponent,
		ChartOfAccountTypeaheadComponent,
		AmountInput,
		InputLabel,
		IfHasPermissionDirective
	],
	templateUrl: './budget-entry.page.html',
	styleUrl: './budget-entry.page.scss'
})
export class BudgetEntryPage extends BaseFormComponent implements OnDestroy, CanComponentDeactivate {

	//#region Dependencies & Template Refs

	private fb = inject(FormBuilder);
	private financialService = inject(FinancialService);
	public userSessionService = inject(UserSessionService);
	private bootstrapService = inject(BootstrapService);
	private router = inject(Router);
	private activatedRoute = inject(ActivatedRoute);
	private injector = inject(Injector);
	private dirtyFormRegistry = inject(DirtyFormRegistryService);
	private destroyRef = inject(DestroyRef);

	public FinancialsPermission = FinancialsPermission;
	public DataEntityTypes = DataEntityTypes;
	public ChartOfAccountCategory = ChartOfAccountCategory;

	@ViewChild('budgetConfirmationModalRef') confirmationModal!: ConfirmationModalComponent;
	@ViewChild('budgetInfoModalRef') infoModal!: InfoModalComponent;

	//#endregion Dependencies & Template Refs

	//#region Form & State

	public pageForm: FormGroup;
	public rowIds: number[] = [];
	private nextRowId = 0;
	private rowSubscriptions: Subscription[] = [];
	private linesVersion = signal(0);

	private resolvedAccounts = signal<Map<number, ChartOfAccountModel>>(new Map());
	public existingBudgetYears = signal<number[]>([]);
	public editingBudgetId = signal<number | null>(null);
	public isLoadingBudget = signal<boolean>(false);
	private loadedBudgetName: string | null = null;
	private redirectAfterSuccess = false;
	// Differentiates which workflow the shared confirmation modal is driving:
	// 'discard' = user clicked Cancel with unsaved changes; 'save' = user clicked Save in edit mode.
	private confirmationFlow = signal<'discard' | 'save' | null>(null);

	public isEditMode = computed<boolean>(() => this.editingBudgetId() !== null);

	//#endregion Form & State

	//#region Computed Permissions

	public requiredSavePermissions = computed<FinancialsPermission[]>(() => {
		return this.isEditMode()
			? [FinancialsPermission.UpdateBudget]
			: [FinancialsPermission.CreateBudget];
	});

	//#endregion Computed Permissions

	//#region Constructor & Initialization

	constructor() {
		super();

		this.dirtyFormRegistry.register(this);
		this.destroyRef.onDestroy(() => this.dirtyFormRegistry.unregister(this));

		const currentYear = new Date().getFullYear();
		this.pageForm = this.fb.group({
			year: [currentYear, [Validators.required, Validators.min(2000), Validators.max(2100), this.takenYearValidator()]],
			lines: this.fb.array([])
		});

		const routeBudgetId = Number(this.activatedRoute.snapshot.paramMap.get('budgetId'));
		if (Number.isFinite(routeBudgetId) && routeBudgetId > 0) {
			this.editingBudgetId.set(routeBudgetId);
		}

		// In edit mode, load the existing budget so we can populate the form with its line items.
		// In create mode, fetch existing budgets so the year validator can reject taken years.
		effect(() => {
			const orgId = this.userSessionService.activeOrganization()?.organizationId;
			if (orgId) {
				if (this.isEditMode()) {
					this.loadBudgetForEdit(this.editingBudgetId()!);
				} else {
					this.loadExistingBudgets(orgId);
				}
			} else {
				this.existingBudgetYears.set([]);
				if (!this.isEditMode()) {
					this.pageForm.get('year')?.setValue(this.computeDefaultYear([]));
					this.pageForm.get('year')?.updateValueAndValidity();
				}
			}
		});
	}

	private async loadBudgetForEdit(budgetId: number): Promise<void> {
		this.isLoadingBudget.set(true);
		try {
			const result = await this.financialService.getBudgetById(budgetId);
			if (!result.success || !result.data) {
				this.infoModal.modalType.set(ModalTypes.Failure);
				this.infoModal.message.set(result.message || BudgetMessagingConstants.BUDGET_LOAD_FAILURE());
				this.bootstrapService.manageModal('#budgetInfoModal', ModalActions.Show);
				return;
			}
			// Deleted budgets are read-only — editing is not allowed even via a direct link.
			if (result.data.status === PropertyStatuses.Deleted) {
				this.router.navigate(['/budgets', budgetId]);
				return;
			}
			this.populateFormFromBudget(result.data);
		} finally {
			this.isLoadingBudget.set(false);
		}
	}

	private populateFormFromBudget(budget: BudgetModel): void {
		this.loadedBudgetName = budget.name ?? null;

		const yearControl = this.pageForm.get('year');
		// Drop the takenYearValidator in edit mode: the year is immutable on PATCH and
		// the budget we're editing will obviously match its own year — we must not flag that as taken.
		yearControl?.setValidators([Validators.required, Validators.min(2000), Validators.max(2100)]);
		yearControl?.setValue(budget.year);
		yearControl?.disable();
		yearControl?.updateValueAndValidity();

		this.clearAllLines();
		const items = budget.budgetLineItems ?? [];
		const minorUnits = this.userSessionService.currentMinorUnits();
		// The budget response already embeds the full account object per line item, so we can seed
		// the resolved-accounts lookup directly here without an extra per-row network round trip.
		const accountsMap = new Map(this.resolvedAccounts());
		for (const item of items) {
			const amountString = CurrencyUtilities.convertMinorUnitsToMajor(item.amount, minorUnits).toFixed(2);
			const chartOfAccountsId = item.account?.chartOfAccountId ?? null;
			if (item.account) accountsMap.set(item.account.chartOfAccountId, item.account);
			const group = this.fb.group({
				chartOfAccountsId: [chartOfAccountsId, [Validators.required]],
				amount: [amountString, [Validators.required, Validators.min(0)]]
			});
			const sub = group.valueChanges.subscribe(() => this.linesVersion.update(v => v + 1));
			this.rowSubscriptions.push(sub);
			this.lines.push(group);
			this.rowIds.push(this.nextRowId++);
		}
		this.resolvedAccounts.set(accountsMap);
		this.linesVersion.update(v => v + 1);
		this.pageForm.markAsPristine();
		this.pageForm.markAsUntouched();
	}

	private clearAllLines(): void {
		this.rowSubscriptions.forEach(sub => sub.unsubscribe());
		this.rowSubscriptions = [];
		while (this.lines.length > 0) {
			this.lines.removeAt(0);
		}
		this.rowIds = [];
	}

	//#endregion Constructor & Initialization

	//#region Data Loading

	private async loadExistingBudgets(orgId: number): Promise<void> {
		const filter: BudgetsFilter = {
			...ListConstants.BUDGETS_FILTER_DEFAULT(),
			organizationIds: [orgId]
		};

		const result = await this.financialService.searchBudgets(filter, BudgetListSortOptions.YearDesc, 0, 100);
		const years = result.success && result.data?.data ? result.data.data.map(b => b.year) : [];

		this.existingBudgetYears.set(years);

		// Only apply the smart default if the user hasn't started editing.
		// Otherwise, an async response could clobber a year the user just typed.
		const yearControl = this.pageForm.get('year');
		if (yearControl && yearControl.pristine && yearControl.untouched) {
			yearControl.setValue(this.computeDefaultYear(years));
		}
		// The validator reads the signal at validation time; nudge re-validation so
		// the 'taken' error reflects freshly loaded data even if the user is editing.
		yearControl?.updateValueAndValidity();
	}

	// Earliest non-taken year ≥ current year, expanding the search up to current+20.
	private computeDefaultYear(taken: number[]): number {
		const current = new Date().getFullYear();
		for (let candidate = current; candidate <= current + 20; candidate++) {
			if (!taken.includes(candidate)) return candidate;
		}
		// Practically unreachable (would require 21 consecutive years of budgets);
		// fall back beyond the searched range so the value is guaranteed un-taken.
		return current + 21;
	}

	private takenYearValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (value == null || value === '') return null;
			const numeric = Number(value);
			if (Number.isNaN(numeric)) return null;
			return this.existingBudgetYears().includes(numeric) ? { taken: true } : null;
		};
	}

	//#endregion Data Loading

	//#region FormArray Management

	public get lines(): FormArray {
		return this.pageForm.get('lines') as FormArray;
	}

	public getLineGroup(index: number): FormGroup {
		return this.lines.at(index) as FormGroup;
	}

	public getAccountForLine(index: number): ChartOfAccountModel | undefined {
		this.linesVersion();
		const id = this.getLineGroup(index).get('chartOfAccountsId')?.value as number | null;
		if (id === null || id === undefined) return undefined;
		return this.resolvedAccounts().get(id);
	}

	// Excludes accounts already chosen by sibling rows from this row's typeahead results.
	// The row's own current selection is never in this list (only other rows contribute),
	// so it always stays visible/resolvable regardless of active/retired state.
	public excludeIdsForRow(index: number): number[] {
		this.linesVersion();
		const excluded: number[] = [];
		this.lines.controls.forEach((control, i) => {
			if (i === index) return;
			const value = control.get('chartOfAccountsId')?.value as number | null;
			if (value !== null && value !== undefined) excluded.push(value);
		});
		return excluded;
	}

	public onAccountSelected(index: number, account: ChartOfAccountModel | undefined): void {
		if (!account) return;
		const map = new Map(this.resolvedAccounts());
		map.set(account.chartOfAccountId, account);
		this.resolvedAccounts.set(map);
	}

	public addRow(): void {
		const group = this.fb.group({
			chartOfAccountsId: [null, [Validators.required]],
			amount: ['', [Validators.required, Validators.min(0)]]
		});
		const sub = group.valueChanges.subscribe(() => {
			this.linesVersion.update(v => v + 1);
		});
		this.rowSubscriptions.push(sub);
		this.lines.push(group);
		this.rowIds.push(this.nextRowId++);
		this.linesVersion.update(v => v + 1);
		this.pageForm.markAsDirty();
	}

	public removeRow(index: number): void {
		this.rowSubscriptions[index]?.unsubscribe();
		this.rowSubscriptions.splice(index, 1);
		this.lines.removeAt(index);
		this.rowIds.splice(index, 1);
		this.linesVersion.update(v => v + 1);
		this.pageForm.markAsDirty();
	}

	//#endregion FormArray Management

	//#region Form Submission

	public async onSubmit(): Promise<void> {
		if (this.pageForm.invalid) {
			this.pageForm.markAllAsTouched();
			// markAllAsTouched doesn't fire valueChanges, so nudge the version
			// signal so the aggregate-error computeds re-evaluate.
			this.linesVersion.update(v => v + 1);
			return;
		}

		if (this.lines.length === 0) {
			this.infoModal.modalType.set(ModalTypes.Failure);
			this.infoModal.message.set(this.FormValidatorMessages.BudgetAtLeastOneLine);
			this.bootstrapService.manageModal('#budgetInfoModal', ModalActions.Show);
			return;
		}

		const orgId = this.userSessionService.activeOrganization()?.organizationId;
		if (!orgId) return;

		if (this.isEditMode()) {
			// Edit-mode save funnels through a confirmation modal that captures an optional audit-log note.
			// The actual PATCH happens in performUpdate(), invoked from the modal's confirmed handler.
			this.openSaveConfirmation();
			return;
		}

		await this.performCreate(orgId);
	}

	private openSaveConfirmation(): void {
		this.confirmationFlow.set('save');
		this.confirmationModal.modalType.set(ModalTypes.Question);
		this.confirmationModal.title.set('Save Budget Changes');
		this.confirmationModal.message.set('Add an optional note describing what changed. This will be recorded in the audit log.');
		this.confirmationModal.confirmButtonText.set('Save');
		this.confirmationModal.cancelButtonText.set('Cancel');
		this.confirmationModal.enableNotes.set(true);
		this.confirmationModal.isNoteRequired.set(false);
		this.bootstrapService.manageModal('#budgetConfirmationModal', ModalActions.Show);
	}

	private collectLineItems(): CreateBudgetLineItemRequest[] {
		return this.lines.controls.map(control => {
			const value = control.value;
			return {
				chartOfAccountsId: value.chartOfAccountsId,
				amount: CurrencyUtilities.convertCurrencyStringToMinorUnits(String(value.amount || '0'), 100)
			};
		});
	}

	private async performCreate(orgId: number): Promise<void> {
		this.isSubmitting = true;
		try {
			const year = this.pageForm.getRawValue().year as number;
			const lineItems = this.collectLineItems();
			const request: CreateBudgetRequest = {
				organizationId: orgId,
				name: `${year} Operating Budget`,
				year,
				frequency: BudgetFrequency.Yearly,
				lineItems
			};
			const result = await this.financialService.createBudget(request);

			if (result.success && result.data) {
				this.pageForm.markAsPristine();
				this.redirectAfterSuccess = true;
				this.infoModal.modalType.set(ModalTypes.Success);
				this.infoModal.message.set(result.message || BudgetMessagingConstants.BUDGET_CREATE_SUCCESS());
				this.bootstrapService.manageModal('#budgetInfoModal', ModalActions.Show);
			} else {
				this.infoModal.modalType.set(ModalTypes.Failure);
				const conflictMessage = ApiResponseUtilities.isConflictStatusCode(result.statusCode)
					? BudgetMessagingConstants.BUDGET_ALREADY_EXISTS_FOR_YEAR()
					: undefined;
				this.infoModal.message.set(conflictMessage || result.message || BudgetMessagingConstants.BUDGET_CREATE_FAILURE());
				this.bootstrapService.manageModal('#budgetInfoModal', ModalActions.Show);
			}
		} catch (error) {
			console.error('Exception caught:', error);
			this.infoModal.modalType.set(ModalTypes.Failure);
			this.infoModal.message.set(BudgetMessagingConstants.BUDGET_CREATE_FAILURE());
			this.bootstrapService.manageModal('#budgetInfoModal', ModalActions.Show);
		} finally {
			this.isSubmitting = false;
		}
	}

	private async performUpdate(note?: string): Promise<void> {
		this.isSubmitting = true;
		try {
			// `pageForm.get('year').value` is null when the control is disabled (edit mode),
			// so read from getRawValue() to capture the disabled year.
			const year = this.pageForm.getRawValue().year as number;
			const lineItems = this.collectLineItems();
			const updateRequest: UpdateBudgetRequest = {
				name: this.loadedBudgetName ?? `${year} Operating Budget`,
				note: note?.trim() || undefined,
				lineItems
			};
			const result = await this.financialService.updateBudget(this.editingBudgetId()!, updateRequest);

			if (result.success && result.data) {
				this.pageForm.markAsPristine();
				this.redirectAfterSuccess = true;
				this.infoModal.modalType.set(ModalTypes.Success);
				this.infoModal.message.set(result.message || BudgetMessagingConstants.BUDGET_UPDATE_SUCCESS());
				this.bootstrapService.manageModal('#budgetInfoModal', ModalActions.Show);
			} else {
				this.infoModal.modalType.set(ModalTypes.Failure);
				this.infoModal.message.set(result.message || BudgetMessagingConstants.BUDGET_UPDATE_FAILURE());
				this.bootstrapService.manageModal('#budgetInfoModal', ModalActions.Show);
			}
		} catch (error) {
			console.error('Exception caught:', error);
			this.infoModal.modalType.set(ModalTypes.Failure);
			this.infoModal.message.set(BudgetMessagingConstants.BUDGET_UPDATE_FAILURE());
			this.bootstrapService.manageModal('#budgetInfoModal', ModalActions.Show);
		} finally {
			this.isSubmitting = false;
		}
	}

	// Enter anywhere in the form adds a new row so users can build a budget without reaching for the mouse.
	// Skipped when focus is on a button (let the button activate) or when a child control
	// already handled the event (e.g. the typeahead selecting a highlighted item).
	public onFormEnter(event: KeyboardEvent): void {
		if (event.defaultPrevented) return;

		const target = event.target as HTMLElement;
		if (target.tagName === 'BUTTON') return;
		if (target.closest('ms-chart-of-account-typeahead')) return;

		event.preventDefault();
		this.addRow();

		// Wait for Angular to flush the new row into the DOM, then focus the
		// newly added row's account typeahead input. afterNextRender runs once
		// after the next render cycle — the idiomatic alternative to setTimeout(0).
		afterNextRender(() => {
			const inputs = document.querySelectorAll<HTMLInputElement>('.budget-line ms-chart-of-account-typeahead input');
			inputs[inputs.length - 1]?.focus();
		}, { injector: this.injector });
	}

	public onCancel(): void {
		if (this.pageForm.dirty) {
			this.confirmationFlow.set('discard');
			this.confirmationModal.modalType.set(ModalTypes.Question);
			this.confirmationModal.title.set('Discard Changes');
			this.confirmationModal.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
			this.confirmationModal.confirmButtonText.set('Discard');
			this.confirmationModal.cancelButtonText.set('Keep Editing');
			this.confirmationModal.enableNotes.set(false);
			this.confirmationModal.isNoteRequired.set(false);
			this.bootstrapService.manageModal('#budgetConfirmationModal', ModalActions.Show);
		} else {
			this.navigateAfterExit();
		}
	}

	public canDeactivate(): boolean | Promise<boolean> {
		return this.awaitLeaveConfirmation(this.pageForm.dirty, () => {
			this.confirmationFlow.set('discard');
			this.confirmationModal.modalType.set(ModalTypes.Question);
			this.confirmationModal.title.set('Discard Changes');
			this.confirmationModal.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
			this.confirmationModal.confirmButtonText.set('Discard');
			this.confirmationModal.cancelButtonText.set('Keep Editing');
			this.confirmationModal.enableNotes.set(false);
			this.confirmationModal.isNoteRequired.set(false);
			this.bootstrapService.manageModal('#budgetConfirmationModal', ModalActions.Show);
		});
	}

	private navigateAfterExit(): void {
		if (this.isEditMode()) {
			this.router.navigate(['/budgets', this.editingBudgetId()]);
		} else {
			this.router.navigate(['/dashboard']);
		}
	}

	//#endregion Form Submission

	//#region Modal Handlers

	public confirmationConfirmFunction(note?: string): void {
		this.bootstrapService.manageModal('#budgetConfirmationModal', ModalActions.Hide);

		if (this.resolvePendingLeave(true)) {
			this.confirmationFlow.set(null);
			return;
		}

		const flow = this.confirmationFlow();
		this.confirmationFlow.set(null);
		if (flow === 'save') {
			this.performUpdate(note);
		} else {
			this.navigateAfterExit();
		}
	}

	public confirmationCancelFunction(): void {
		this.bootstrapService.manageModal('#budgetConfirmationModal', ModalActions.Hide);

		if (this.resolvePendingLeave(false)) {
			this.confirmationFlow.set(null);
			return;
		}

		this.confirmationFlow.set(null);
	}

	public infoModalConfirmed(): void {
		this.bootstrapService.manageModal('#budgetInfoModal', ModalActions.Hide);
		if (this.redirectAfterSuccess) {
			this.redirectAfterSuccess = false;
			if (this.isEditMode()) {
				this.router.navigate(['/budgets', this.editingBudgetId()]);
			} else {
				this.router.navigate(['/budgets']);
			}
		}
	}

	//#endregion Modal Handlers

	//#region UI Helpers

	public formatAccountCategoryLabel(category: ChartOfAccountCategory): string {
		return category === ChartOfAccountCategory.Revenue ? 'Revenue' : 'Expense';
	}

	public totalRevenue = computed<number>(() => this.totalByCategory(ChartOfAccountCategory.Revenue));
	public totalExpenses = computed<number>(() => this.totalByCategory(ChartOfAccountCategory.Expenses));

	public hasInvalidAmountRow = computed<boolean>(() => {
		this.linesVersion();
		return this.lines.controls.some(c => {
			const amount = c.get('amount');
			return !!amount && amount.touched && amount.invalid;
		});
	});

	public hasMissingAccountRow = computed<boolean>(() => {
		this.linesVersion();
		return this.lines.controls.some(c => {
			const account = c.get('chartOfAccountsId');
			return !!account && account.touched && account.invalid;
		});
	});

	private totalByCategory(category: ChartOfAccountCategory): number {
		this.linesVersion();
		return this.lines.controls.reduce((sum, control) => {
			const id = control.get('chartOfAccountsId')?.value as number | null;
			if (id === null || id === undefined) return sum;
			const account = this.resolvedAccounts().get(id);
			if (!account || account.category !== category) return sum;
			const amount = parseFloat(String(control.get('amount')?.value || '0').replace(/,/g, ''));
			return isNaN(amount) ? sum : sum + amount;
		}, 0);
	}

	public formatCurrency(amount: number): string {
		const minorAmount = CurrencyUtilities.convertMajorUnitsToMinor(amount, this.userSessionService.currentMinorUnits());
		return CurrencyUtilities.convertMinorUnitToDisplayFormat(minorAmount, this.userSessionService.currentCurrency());
	}

	//#endregion UI Helpers

	//#region Lifecycle

	public ngOnDestroy(): void {
		this.rowSubscriptions.forEach(sub => sub.unsubscribe());
	}

	//#endregion Lifecycle
}
